/**
 * Separates field-specific errors from general errors
 * Returns only errors that are NOT related to specific fields
 */
export function getGeneralErrors(
  allErrors: string[],
  fieldErrors: Record<string, string[]>
): string[] {
  if (!allErrors || allErrors.length === 0) return [];
  if (!fieldErrors || Object.keys(fieldErrors).length === 0) return allErrors;

  // Get all field-specific error messages
  const fieldErrorMessages = new Set<string>();
  Object.values(fieldErrors).forEach((errors) => {
    errors.forEach((error) => {
      fieldErrorMessages.add(error);
    });
  });

  // Filter out errors that are field-specific
  return allErrors.filter((error) => {
    // Check if this error message matches any field error (case-insensitive partial match)
    const lowerError = error.toLowerCase();
    return !Array.from(fieldErrorMessages).some((fieldError) =>
      lowerError.includes(fieldError.toLowerCase())
    );
  });
}

/**
 * Parses authentication error responses into user-friendly messages
 * Excludes field-specific errors if field_errors are present
 */
export function parseAuthError(
  err: any,
  defaultMessage: string = 'An error occurred. Please try again.'
): string {
  if (err.response?.data?.error) {
    return err.response.data.error;
  }

  if (err.response?.data?.errors) {
    const errors = Array.isArray(err.response.data.errors)
      ? err.response.data.errors
      : [err.response.data.errors];

    // If field_errors exist, filter out field-specific errors
    if (err.response?.data?.field_errors) {
      const generalErrors = getGeneralErrors(
        errors,
        err.response.data.field_errors
      );
      if (generalErrors.length > 0) {
        return generalErrors.join(', ');
      }
      // If all errors are field-specific, return empty string
      return '';
    }

    return errors.join(', ');
  }

  if (err.message) {
    return err.message;
  }

  return defaultMessage;
}

/**
 * Common authentication form validation
 */
export function validateAuthForm(
  formData: Record<string, string>,
  requiredFields: string[]
): string | null {
  for (const field of requiredFields) {
    if (!formData[field] || formData[field].trim() === '') {
      return `${field.replace('_', ' ')} is required`;
    }
  }
  return null;
}
